// @refresh reset
// frontend/src/auth/AuthContext.tsx
import { createContext, useContext, useEffect, useState } from "react";
import { fetchMe, login as apiLogin, logoutUser } from "../api/auth";
import { resetUserScopedState } from "../store/resetState";

export type User = {
  id: string;
  email: string;
  role: "admin" | "analyst" | "client" | "super_admin" | string;
  industry?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  organization?: string;
  job_title?: string;
  created_at?: string;
  last_login?: string;
  permissions?: Record<string, boolean>;
  show_locked_tabs?: boolean;
  plan?: string;
  subscription_status?: string;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  logout: () => void;
  login: (email: string, password: string, turnstile_token?: string) => Promise<string>;
  refreshUser: () => Promise<User | null>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem("user_data");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  async function refreshUser(): Promise<User | null> {
    try {
      const me = await fetchMe();
      setUser(me);
      if (me?.role) {
        localStorage.setItem("role", me.role);
      }
      if (me) {
        localStorage.setItem("user_data", JSON.stringify(me));
      }
      return me;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    async function init() {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setUser(null);
          localStorage.removeItem("user_data");
          setLoading(false);
          return;
        }

        await refreshUser();
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    }

    init();
  }, []);

  async function login(email: string, password: string, turnstile_token?: string) {
    const role = await apiLogin(email, password, turnstile_token);
    const me = await refreshUser();
    localStorage.setItem("role", role);
    if (me) {
      localStorage.setItem("user_data", JSON.stringify(me));
    }
    return role;
  }

  async function logout() {
    try {
      await logoutUser();
    } catch {
      // Ignore network errors on logout
    } finally {
      resetUserScopedState();
      localStorage.removeItem("user_data");
      localStorage.removeItem("access_token");
      localStorage.removeItem("role");
      setUser(null);
      window.location.href = "/login";
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
